import { FormValidationFunction, FormValidationResult } from "../../../../form/formValidation";
import { DateTime } from "luxon";
import { OrderFormState, OrderFormStateValues } from "./OrderFormState";
import { Amount, TimeInForce } from "@goodfill/domain-properties";
import { formatCurrencyAmount } from "@goodfill/format-number";
import { plusBusinessDay, isUsMarketOpen, getUsMarketOpenness } from "@goodfill/business-days";
import { TIMEZONE_NEY_YORK } from "../../../../locale/timezone";

export type CreateOrderStateValidated = Pick<
  OrderFormStateValues,
  "faceAmount" | "expirationDate" | "priceVsYield" | "customLoadedPrice" | "customLoadedYield"
>;

interface ExtraValidationParams {
  currentDate: DateTime;
  availableBalance: Amount;
}

function validateFaceAmount(state: OrderFormState, extra: ExtraValidationParams) {
  const faceAmountValidation: FormValidationResult = [];

  const amount = state.s.faceAmount.amount;
  const availableToSell = state.i.availableToSell;

  const minimumPiece = state.i.pricedBond.minimumPiece.amount;

  if (amount.lessThanOrEqualTo(0)) {
    faceAmountValidation.push("Face Amount is required");
  } else {
    if (amount.lessThan(state.i.pricedBond.minimumPiece.amount)) {
      faceAmountValidation.push("Face Amount must be more then Bond's Minimum Piece");
    }
  }

  if (availableToSell) {
    if (amount.greaterThan(availableToSell)) {
      faceAmountValidation.push(
        `Face Amount must be less then what is Available to Sell (${formatCurrencyAmount({
          value: availableToSell.toFixed(0),
          numDecimals: 0,
        })}).`,
      );
    } else {
      const remainingAmount = availableToSell.minus(amount);

      if (remainingAmount.lessThan(minimumPiece) && !remainingAmount.equals("0")) {
        faceAmountValidation.push(
          `Remaining amount is less then Minimum Piece (${formatCurrencyAmount({
            value: minimumPiece.toFixed(0),
            numDecimals: 0,
          })}). You have to sell whole amount or leave at least the minimum piece.`,
        );
      }
    }
  }

  if (state.isBuy() && extra.availableBalance.lessThan(state.settlementAmount)) {
    faceAmountValidation.push("Insufficient Balance");
  }

  return faceAmountValidation;
}

function getErrorForClosedMarketToUseFuture(date: DateTime) {
  const nextBusinessDay = plusBusinessDay(date.plus({ days: 1 }), TIMEZONE_NEY_YORK, 0);
  return `Market is currently closed. Please select Future and set the order to expire at or after 9am ET on ${nextBusinessDay.toLocaleString(
    DateTime.DATE_MED,
  )}.`;
}

function validateExpirationDate(state: OrderFormState, extra: ExtraValidationParams) {
  const expirationDateValidation: FormValidationResult = [];
  const zonedCurrentTime = getUsMarketOpenness(extra.currentDate);

  if (state.s.expirationDate === null) {
    if (!state.isImmediate()) {
      expirationDateValidation.push(
        `Expiration ${
          state.s.timeInForce === TimeInForce.CustomToday ? "time" : "date"
        } is required for Custom Order.`,
      );
    } else if (zonedCurrentTime === "After") {
      expirationDateValidation.push(getErrorForClosedMarketToUseFuture(extra.currentDate));
    } else if (zonedCurrentTime === "Before") {
      expirationDateValidation.push("Market will be opening soon.");
    } else if (zonedCurrentTime === "OffDay") {
      expirationDateValidation.push("Market is closed today.");
    }
    return expirationDateValidation;
  }

  if (state.s.expirationDate < extra.currentDate) {
    expirationDateValidation.push("Expiration date must be in the future");
  }

  if (state.isCustomFuture() && !isUsMarketOpen(state.s.expirationDate)) {
    expirationDateValidation.push("Expiration date and time must be between 9am - 5pm ET on a Business Day.");
  }

  return expirationDateValidation;
}

function validatePrice(state: OrderFormState) {
  if (state.s.priceVsYield !== "price" || state.isImmediate()) {
    return [];
  }

  if (state.isBuy() && state.s.customLoadedPrice.greaterThan(state.c.defaultLoadedPrice)) {
    return [`Maximum price is ${state.c.defaultLoadedPrice.mul(100).toFixed(2)}`];
  }

  if (state.isSell() && state.s.customLoadedPrice.lessThan(state.c.defaultLoadedPrice)) {
    return [`Minimum price is ${state.c.defaultLoadedPrice.mul(100).toFixed(2)}`];
  }

  return [];
}

function validateYield(state: OrderFormState) {
  if (state.s.priceVsYield !== "yield" || state.isImmediate()) {
    return [];
  }

  if (state.isBuy() && state.s.customLoadedYield.lessThan(state.c.defaultLoadedYield)) {
    return [`Minimum yield is ${state.c.defaultLoadedYield.mul(100).toFixed(2)}%`];
  }

  if (state.isSell() && state.s.customLoadedYield.greaterThan(state.c.defaultLoadedYield)) {
    return [`Maximum yield is ${state.c.defaultLoadedYield.mul(100).toFixed(2)}%`];
  }

  return [];
}

export const validateCreateOrderState: FormValidationFunction<
  OrderFormState,
  ExtraValidationParams,
  CreateOrderStateValidated
> = (state: OrderFormState, extra: ExtraValidationParams) => {
  return {
    faceAmount: validateFaceAmount(state, extra),
    expirationDate: validateExpirationDate(state, extra),
    priceVsYield: [],
    customLoadedPrice: validatePrice(state),
    customLoadedYield: validateYield(state),
  };
};
